

// Those values will be translated in a more friendly way
export const DURATIONS: DurationType[] = ['0.5h', '1h', '2h', '3h', '4h', '6h', '8h',];


export type DurationType = "0.5h" | "1h" | "2h" | "3h" | "4h" | "6h" | "8h";


/**
 * Calculates the end time by adding duration to start time
 * 
 * @param startTime - Start time in HH:mm format (e.g., "09:30")
 * @param duration - Duration in format like "0.5h", "2h", "3h", etc.
 * @returns End time in HH:mm format or null if inputs are invalid
 */
export function calculateEndTime(startTime: string | null, duration: DurationType | null): string | null {
  if (!startTime || !duration) {
    return null;
  }

  try {
    // Parse start time
    const [hours, minutes] = startTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    // Extract duration hours (may be fractional, e.g. "0.5h")
    const durationHours = parseFloat(duration.replace('h', ''));
    if (isNaN(durationHours)) {
      return null;
    }

    // Calculate end time
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + Math.round(durationHours * 60));

    // Format back to HH:mm
    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
  } catch (error) {
    console.error('Error calculating end time:', error);
    return null;
  }
}

/**
 * Converts "HH:mm" time string to minutes from start of day
 */
export function timeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const parts = time.split(':');
  if (parts.length !== 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * Checks if two time intervals overlap.
 * Intervals are inclusive at start, exclusive at end [start, end).
 */
export function isTimeConflict(
  start1: string | null | undefined,
  end1: string | null | undefined,
  start2: string | null | undefined,
  end2: string | null | undefined
): boolean {
  const s1 = timeToMinutes(start1);
  const e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  const e2 = timeToMinutes(end2);

  if (s1 === null || e1 === null || s2 === null || e2 === null) return false;

  // Standard overlap check: (StartA < EndB) and (EndA > StartB)
  return s1 < e2 && e1 > s2;
}

/**
 * Compares two time strings for sorting.
 * null values are considered "greater" (placed at the end).
 */
export function compareTimes(t1: string | null | undefined, t2: string | null | undefined): number {
  if (!t1 && !t2) return 0;
  if (!t1) return 1;
  if (!t2) return -1;
  return t1.localeCompare(t2);
}